import React from 'react';
import InfoPageLayout from './InfoPageLayout';

export default function PrivacyPage() {
  const sections = [
    {
      heading: 'What we collect',
      content: (
        <>
          <p>
            We collect the minimum information needed to run Lilycove, including account details (if you sign up),
            search and navigation activity, marketplace clickouts, device/browser metadata, and basic technical logs.
            We also capture referral and campaign metadata (for example, UTM parameters) to understand acquisition and
            product usage.
          </p>
          <p>
            We do not sell your personal information. We do not collect payment card data in Lilycove today.
          </p>
        </>
      ),
    },
    {
      heading: 'How we use it',
      content: (
        <>
          <p>
            We use collected data to operate the product, improve search quality, measure reliability, detect abuse,
            and understand which features create value for the community. We also use aggregated metrics for business
            planning and platform health monitoring.
          </p>
          <p>
            Where possible, we use aggregated or pseudonymous analytics instead of directly identifying data.
          </p>
        </>
      ),
    },
    {
      heading: 'Analytics tools',
      content: (
        <>
          <p>
            Lilycove uses first-party analytics as its primary source of truth. We may also use supplemental tools
            such as Google Analytics and Microsoft Clarity to understand acquisition and product behavior. These tools
            are enabled based on consent choices where required.
          </p>
          <p>
            We configure analytics to focus on product performance and usage trends, and we avoid collecting
            unnecessary sensitive data.
          </p>
        </>
      ),
    },
    {
      heading: 'Data retention',
      content: (
        <>
          <p>
            We retain operational and analytics data only as long as needed for product operation, security, legal
            obligations, and reporting integrity. Retention periods may vary by data type (for example, raw events
            versus aggregated metrics).
          </p>
          <p>
            When data is no longer needed, we delete it or de-identify it using reasonable administrative and technical
            controls.
          </p>
        </>
      ),
    },
    {
      heading: 'Marketplace listing data (eBay)',
      content: (
        <>
          <p>
            Lilycove shows public eBay listings retrieved through eBay&apos;s official APIs: title, price, condition,
            item location, photo and a link to the listing on eBay. With each listing we also receive the seller&apos;s
            public eBay user name and feedback score; we keep them only with that listing and do not display, sell or
            share them, or use them to contact anyone.
          </p>
          <p>
            When eBay notifies us that a member has closed their eBay account, we remove that member&apos;s user name
            and feedback score from our records. Prices and availability are as of when we last checked eBay; the eBay
            listing page is always current.
          </p>
        </>
      ),
    },
    {
      heading: 'Third-party links',
      content: (
        <>
          <p>
            Lilycove links to third-party marketplaces and external websites. When you leave Lilycove, the destination
            site&apos;s privacy policy and terms apply. We are not responsible for third-party privacy practices outside
            Lilycove-controlled properties.
          </p>
        </>
      ),
    },
    {
      heading: 'Your rights (PIPEDA and Quebec Law 25)',
      content: (
        <>
          <p>
            If you are in Canada, including Quebec, you may have rights to access, correction, and deletion of your
            personal information, subject to applicable legal limits. You may also withdraw consent for certain
            analytics processing where consent is the legal basis.
          </p>
          <p>
            We handle requests in line with applicable Canadian privacy requirements, including principles under
            PIPEDA and obligations introduced by Quebec Law 25.
          </p>
        </>
      ),
    },
    {
      heading: 'Contact',
      content: (
        <>
          <p>
            For privacy requests or questions, contact us at{' '}
            {/* TODO(launch): replace placeholder privacy contact email before production launch. */}
            <a className="text-purple hover:text-purple/90 underline" href="mailto:privacy@lilycove.io">
              privacy@lilycove.io
            </a>
            .
          </p>
        </>
      ),
    },
    {
      heading: 'Updates',
      content: (
        <>
          <p>
            We may update this Privacy Policy as Lilycove evolves. Material changes will be reflected by updating the
            date above and, where appropriate, through in-product notice.
          </p>
        </>
      ),
    },
  ];

  return (
    <InfoPageLayout
      title="Privacy Policy | Lilycove"
      description="Read how Lilycove handles privacy and personal data."
      heading="Privacy Policy"
      subtitle="Last updated: March 2026"
      containerClassName="max-w-[720px] min-h-[70vh] rounded-lg !bg-[#1A1A20] p-6 md:p-10"
      headingClassName="text-purple"
    >
      <div className="space-y-8">
        {sections.map((section) => (
          <section key={section.heading} className="space-y-3">
            <div className="border-b border-purple/30 pb-2">
              <h2 className="text-lg font-medium text-cream">{section.heading}</h2>
            </div>
            <div className="space-y-3 text-cream/80 leading-relaxed">{section.content}</div>
          </section>
        ))}
      </div>
    </InfoPageLayout>
  );
}
